import useSWRMutation from "swr/mutation";

// Helpers
import api from "frontend/js/api";
import helpers from "frontend/js/helpers";
import shopifyHelper from "../../js/shopify";
import {hasher } from "../../../frontend/js/alpine/stores/hasher";

// import UPSERT_METAOBJECT from '../../js/graphql/2024_01/metaobject/upsert.graphql';

const baseUrl = api.getAdminGraphqlBaseUrl();

// Fetch lists
// Get query
const getFetchCustomerListsQuery = () => {
    return `
    query GetCustomerLists($id: ID!) {
      customer(id: $id) {
        wishlistLists: metafield (namespace: "custom", key: "wishlist_lists") {
          references(first: 50) {
            edges {
              node {
                ... on Metaobject {
                  id
                  handle
                  fields {
                    key
                    value
                  }
                }
              }
            }
          }
        }
      }
    }
  `;
};

// Fetcher
const fetchCustomerListsMutation = async (query, { arg }) => {
    const { customerId } = arg;

    await hasher.store().dispatchHash();
    const res = {
      options: {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "X-Shopify-Access-Token": '<SHOPIFY_API_AUTHENTICATED_KEY>',
            'X-MindArc-Hash': hasher.store().getHash()
        },
        body: JSON.stringify({
            query,
            variables: { id: `gid://shopify/Customer/${customerId}` },
        }),
      }
    };

    let jsonResponse = null;
    jsonResponse = await shopifyHelper.authenticatedFetch(res);

    if (!jsonResponse.ok) console.log("Failed to fetch customer wishlists");

    const { data } = jsonResponse;

    return data.customer.wishlistLists?.references?.edges.map((edge) => ({
        id: edge.node.id,
        handle: edge.node.handle,
        title: helpers.getObjectFromKeyAndValue(
            edge.node.fields,
            "key",
            "list_name"
        )?.value,
        listType: helpers.getObjectFromKeyAndValue(
            edge.node.fields,
            "key",
            "wishlist_id"
        )?.value,
        products: JSON.parse(
            helpers.getObjectFromKeyAndValue(
                edge.node.fields,
                "key",
                "products"
            )?.value
        ),
    }));
};

// Update list
// Get query
// UpsertMetaobject mutation to update exiting metaobject or create if not exits
const getUpdateCustomerListQuery = () => {
    return `
    mutation UpsertMetaobject($handle: MetaobjectHandleInput!, $metaobject: MetaobjectUpsertInput!) {
      metaobjectUpsert(handle: $handle, metaobject: $metaobject) {
        metaobject {
          id
          handle
          fields {
            key
            value
          }
        }
        userErrors {
          field
          message
          code
        }
      }
    }
  `;
};

const getUpdateCustomerListInput = (
    handle: string,
    listName: string,
    products: any
) => {
    return {
        handle: {
            type: "wishlist",
            handle,
        },
        metaobject: {
            // Pre-defined fields
            fields: [
                {
                    key: "list_name",
                    value: listName,
                },
                {
                    key: "wishlist_id",
                    value: handle,
                },
                {
                    key: "products",
                    value: JSON.stringify(products),
                },
            ],
            capabilities: {
                publishable: {
                    status: "ACTIVE",
                },
            },
        },
    };
};

// Fetcher
const updateCustomerListMutation = async (query, { arg }) => {
    await hasher.store().dispatchHash();
    const { handle, listName, products } = arg;

    let res = {
      options: {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "X-Shopify-Access-Token": '<SHOPIFY_API_AUTHENTICATED_KEY>',
            'X-MindArc-Hash': hasher.store().getHash()
        },
        body: JSON.stringify({
            query,
            variables: getUpdateCustomerListInput(handle, listName, products),
        }),
      }
    };

    let jsonResponse = null;
    jsonResponse = await shopifyHelper.authenticatedFetch(res);

    const { data } = jsonResponse;

    return data.metaobjectUpsert.metaobject;
};

// Add list to customer
const getCreateCustomerListQuery = () => {
    return `
    mutation updateCustomerMetafields($input: CustomerInput!) {
      customerUpdate(input: $input) {
        customer {
          id
          metafields(first: 3) {
            edges {
              node {
                id
                namespace
                key
                value
              }
            }
          }
        }
        userErrors {
          message
          field
        }
      }
    }
  `;
};

const getCreateCustomerListInput = (
    customerId: Number,
    wishlistLists: string[]
) => {
    return {
        input: {
            metafields: [
                {
                    namespace: "custom",
                    key: "wishlist_lists",
                    value: JSON.stringify(wishlistLists),
                },
            ],
            id: `gid://shopify/Customer/${customerId}`,
        },
    };
};

// Fetcher
const createCustomerListMutation = async (query, { arg }) => {
    await hasher.store().dispatchHash();
    const { customerId, wishlistLists } = arg;

    const res = {
      options: {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "X-Shopify-Access-Token": '<SHOPIFY_API_AUTHENTICATED_KEY>',
            'X-MindArc-Hash': hasher.store().getHash()
        },
        body: JSON.stringify({
            query,
            variables: getCreateCustomerListInput(customerId, wishlistLists),
        }),
      }
    };

    let jsonResponse = null;
    jsonResponse = await shopifyHelper.authenticatedFetch(res);

    const { data } = jsonResponse;

    return data;
};

const useCustomerWishlist = () => {
    // Fetch customer lists
    const {
        data: customerLists,
        trigger: fetchCustomerLists,
        isMutating: isLoadingWishlist,
    } = useSWRMutation(
        getFetchCustomerListsQuery(),
        fetchCustomerListsMutation
    );

    // Update customer list
    const { trigger: updateCustomerList } = useSWRMutation(
        getUpdateCustomerListQuery(),
        updateCustomerListMutation
    );

    // Create customer list
    const { trigger: createCustomerList } = useSWRMutation(
        getCreateCustomerListQuery(),
        createCustomerListMutation
    );

    return {
        customerLists,
        fetchCustomerLists,
        isLoadingWishlist,
        updateCustomerList,
        createCustomerList,
    };
};

export default useCustomerWishlist;

